import { alpha, type Theme } from '@mui/material/styles'
import { Check as CheckIcon, Close as CloseIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

/**
 * Оформление вердикта автопроверки в разборе работы — одно на все рендереры
 * и на всех смотрящих: ученика, учителя на проверке и разбор теста уровня.
 *
 * Раньше вердикт красил сам текст ответа в зелёный или красный. Зелёный
 * `success.main` даже на белом даёт 2.5:1, а на подложке выключенного поля
 * терялся совсем — ученик с верными ответами их не мог прочитать. Теперь
 * текст остаётся основным цветом, а вердикт несут рамка, лёгкая подложка и
 * значок: так цвет больше не спорит с читаемостью и не единственный носитель
 * смысла.
 */

export type VerdictOutcome = 'correct' | 'wrong' | 'skipped'

/** Толщина рамки вердикта: обычная рамка поля в 1px на подложке не видна */
export const VERDICT_BORDER_WIDTH = 2

/** Подложка лёгкая: её дело — подкрасить, а не спорить с текстом */
const VERDICT_TINT_OPACITY = 0.1

/**
 * Цвет вердикта — для рамки и значка, не для текста.
 *
 * В светлой теме берём тёмный тон: `success.main` на светлом фоне не
 * дотягивает даже до 3:1, `success.dark` — 4.7:1. В тёмной теме основной тон
 * и так светлый (7.9:1 на поверхности), а тёмный на ней бы потерялся.
 * Пропущенное — нейтральным серым: цвет ошибки на пустом поле обвиняет
 * ученика в том, чего он не делал.
 */
export function verdictAccent(theme: Theme, outcome: VerdictOutcome): string {
  if (outcome === 'skipped') return theme.palette.text.secondary

  return verdictToneAccent(theme, outcome === 'correct' ? 'success' : 'error')
}

export function verdictTint(theme: Theme, outcome: VerdictOutcome): string {
  return alpha(verdictAccent(theme, outcome), VERDICT_TINT_OPACITY)
}

/**
 * Тон вердикта по палитре — для мест, где исходов больше, чем «верно/неверно»:
 * «оценит учитель» у перевода, «пропущено» в пословном разборе диктанта.
 */
export type VerdictTone = 'success' | 'warning' | 'error'

/** Цвет тона для рамки и значка — по тому же правилу светлой и тёмной темы, что {@link verdictAccent} */
export function verdictToneAccent(theme: Theme, tone: VerdictTone): string {
  const palette = theme.palette[tone]

  return theme.palette.mode === 'dark' ? palette.main : palette.dark
}

export function verdictToneTint(theme: Theme, tone: VerdictTone): string {
  return alpha(verdictToneAccent(theme, tone), VERDICT_TINT_OPACITY)
}

/** Пропущенное — пунктиром: «здесь пусто», а не «здесь ответ» */
export function verdictBorderStyle(outcome: VerdictOutcome): 'solid' | 'dashed' {
  return outcome === 'skipped' ? 'dashed' : 'solid'
}

/**
 * Рамка, подложка и цвет текста проверенного элемента: фишки, строки порядка,
 * пропуска с перетаскиванием.
 *
 * Только длинные свойства рамки, без `border`: в `sx` порядок ключей решает,
 * и сокращённая запись, оказавшись ниже `borderColor`, сбросила бы цвет.
 */
export function verdictFrameSx(theme: Theme, outcome: VerdictOutcome) {
  return {
    borderWidth: VERDICT_BORDER_WIDTH,
    borderStyle: verdictBorderStyle(outcome),
    borderColor: verdictAccent(theme, outcome),
    backgroundColor: verdictTint(theme, outcome),
    color: theme.palette.text.primary,
  }
}

const VERDICT_ICONS: Record<VerdictOutcome, typeof CheckIcon> = {
  correct: CheckIcon,
  wrong: CloseIcon,
  skipped: RemoveIcon,
}

/**
 * Значок вердикта: галочка, крестик, прочерк.
 *
 * Компонентом, а не элементом — выключенный `Select` принимает его в
 * `IconComponent` вместо стрелки: открыть проверенный список всё равно нельзя.
 */
export function verdictIcon(outcome: VerdictOutcome): typeof CheckIcon {
  return VERDICT_ICONS[outcome]
}

/** Вердикт элемента без понятия «пропущено»: пары, порядок, группы */
export function verdictOutcomeOf(verdict: boolean | null | undefined): VerdictOutcome | undefined {
  if (verdict === null || verdict === undefined) return undefined

  return verdict ? 'correct' : 'wrong'
}

/**
 * Подпись вердикта для `title`.
 *
 * При дальтонизме рамки одного цвета, и без слова верное от неверного не
 * отличить. Элемент без вердикта подписи не получает — подсказывать там нечего.
 */
export function useVerdictTitle() {
  const { t } = useTranslation()

  return (outcome: VerdictOutcome | undefined): string | undefined => {
    if (outcome === undefined) return undefined
    if (outcome === 'skipped') return t('exercises.run.checkSkipped')

    return outcome === 'correct' ? t('exercises.run.checkCorrect') : t('exercises.run.checkWrong')
  }
}
